import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {api} from "../ui/api";

// Аутентификация пользователя
export const authUser = createAsyncThunk(
    'form/authUser',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await api.post("/student/auth/token/", formData);
            const data = response.data;
            if (data.access) {
                localStorage.setItem("token", data.access);
            }
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.detail || "Authorization error");
        }
    }
);

// Регистрация пользователя
export const registerUser = createAsyncThunk(
    'form/registerUser',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await api.post("/student/auth/register/", formData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Registration error");
        }
    }
);

// Обновление токена
export const refreshToken = createAsyncThunk(
    'form/refreshToken',
    async (refreshToken, { rejectWithValue }) => {
        try {
            const response = await api.post("/student/auth/token/refresh/", {
                refresh: refreshToken
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Token refresh error");
        }
    }
);

// Подтверждение email
export const verifyEmail = createAsyncThunk(
    'form/verifyEmail',
    async (verificationData, { rejectWithValue }) => {
        try {
            const response = await api.post("/student/auth/verify-email/", verificationData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Email verification error");
        }
    }
);

// Получение данных пользователя
export const getUser = createAsyncThunk(
    'form/getUser',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get("/student/student/detail/");
            return response.data;
        } catch (error) {
            localStorage.removeItem("token");
            return rejectWithValue(error.response?.data?.detail || "Authorization error");
        }
    }
);

// Обновление профиля пользователя
export const updateStudentProfile = createAsyncThunk(
    'form/updateStudentProfile',
    async (profileData, { rejectWithValue }) => {
        try {
            const response = await api.patch("/student/student/update/", profileData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Profile update error");
        }
    }
);

// Обновление фото пользователя
export const patchPhotoUser = createAsyncThunk(
    'form/patchPhotoUser',
    async (photo, { rejectWithValue }) => {
        try {
            const formData = new FormData()
            formData.append("photo", photo)
            const response = await api.patch("/student/student/update_photo/", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.detail || "Photo update error");
        }
    }
);

// Сброс пароля
export const newPasswordUser = createAsyncThunk(
    'form/newPasswordUser',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await api.patch("/student/reset-password/", formData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.detail || "Password reset error");
        }
    }
);

// Удаление аккаунта
export const deleteAccount = createAsyncThunk(
    'form/deleteAccount',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.delete("/student/student/delete/");
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Account deletion error");
        }
    }
);

// Получение курсов студента
export const getStudentCourses = createAsyncThunk(
    'form/getStudentCourses',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get("/courses/student/courses/");
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Courses fetch error");
        }
    }
);

// Получение тем курса
export const getCourseTopics = createAsyncThunk(
    'form/getCourseTopics',
    async (courseId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/courses/student/${courseId}/topics/`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Topics fetch error");
        }
    }
);

// Отправка ответов на вопросы
export const submitAnswer = createAsyncThunk(
    'form/submitAnswer',
    async (answerData, { rejectWithValue }) => {
        try {
            const response = await api.post("/courses/answers/", answerData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Answer submission error");
        }
    }
);

// Отправка feedback
export const submitFeedback = createAsyncThunk(
    'form/submitFeedback',
    async (feedbackData, { rejectWithValue }) => {
        try {
            const response = await api.post("/courses/feedback/", feedbackData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Feedback submission error");
        }
    }
);

// Создание платежа
export const createPayment = createAsyncThunk(
    'form/createPayment',
    async (paymentData, { rejectWithValue }) => {
        try {
            const response = await api.post("/courses/create-payment/", paymentData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Payment creation error");
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState: {
        user: null,
        email: '',
        password: '',
        loading: false,
        error: null,
        success: false,
        errorNewPassword: null,
        isAuthenticated: false,
        token: localStorage.getItem("token") || null,
        refreshToken: localStorage.getItem("refreshToken") || null,
        courses: [],
        currentCourseTopics: [],
        paymentData: null
    },
    reducers: {
        setAuthData: (state, action) => {
            const { field, value } = action.payload;
            state[field] = value;
        },
        logout: (state) => {
            state.isAuthenticated = false;
            state.token = null;
            state.user = null;
            state.refreshToken = null;
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
        },
        clearError: (state) => {
            state.error = null;
            state.errorNewPassword = null;
        },
        setCourses: (state, action) => {
            state.courses = action.payload;
        },
        clearPaymentData: (state) => {
            state.paymentData = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Auth User
            .addCase(authUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(authUser.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.token = action.payload.access;
                state.refreshToken = action.payload.refresh;
                state.isAuthenticated = true;
                localStorage.setItem("token", action.payload.access);
                localStorage.setItem("refreshToken", action.payload.refresh);
            })
            .addCase(authUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.isAuthenticated = false;
            })

            // Register User
            .addCase(registerUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(registerUser.fulfilled, (state) => {
                state.loading = false;
                state.success = true;
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Get User
            .addCase(getUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
            })
            .addCase(getUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.isAuthenticated = false;
            })

            // Update Student Profile
            .addCase(updateStudentProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateStudentProfile.fulfilled, (state, action) => {
                state.loading = false;
                if (state.user) {
                    state.user = { ...state.user, ...action.payload };
                }
            })
            .addCase(updateStudentProfile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Patch Photo
            .addCase(patchPhotoUser.fulfilled, (state, action) => {
                state.loading = false;
                if (state.user) {
                    state.user = { ...state.user, photo: action.payload.photo };
                }
            })
            .addCase(patchPhotoUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // New Password
            .addCase(newPasswordUser.pending, (state) => {
                state.loading = true;
                state.errorNewPassword = null;
            })
            .addCase(newPasswordUser.fulfilled, (state) => {
                state.loading = false;
                state.success = true;
            })
            .addCase(newPasswordUser.rejected, (state, action) => {
                state.loading = false;
                state.errorNewPassword = action.payload;
            })

            // Delete Account
            .addCase(deleteAccount.fulfilled, (state) => {
                state.loading = false;
                state.isAuthenticated = false;
                state.user = null;
                state.token = null;
                localStorage.removeItem("token");
            })

            // Get Student Courses
            .addCase(getStudentCourses.fulfilled, (state, action) => {
                state.courses = action.payload;
            })

            // Get Course Topics
            .addCase(getCourseTopics.fulfilled, (state, action) => {
                state.currentCourseTopics = action.payload;
            })

            // Create Payment
            .addCase(createPayment.fulfilled, (state, action) => {
                state.paymentData = action.payload;
            });
    },
});

export const { setAuthData, logout, clearError, setCourses, clearPaymentData } = authSlice.actions;
export default authSlice.reducer;